import { IWebClient } from "@cd2/skeem-client"
import { getClearancePriceForProduct } from "../../shared/operatingRegions"
import { IRecord } from "../types/schema"
import { sortBy } from "./arrays"

const productAttrsForCalculateCostPriceFromCurrency = [
  "cost_price_pennies",
  { name: "preferred_supplier", attributes: ["currency_name"] },
  { name: "suppliers", attributes: ["currency_name"] },
  { name: "product_group", attributes: ["products_price_method"] },
  { name: "branch", attributes: ["currency_name"] },
]

type IProduct = IRecord<"products", "sale_price_pennies" | "cost_price_pennies"> & {
  preferred_supplier?: IRecord<"suppliers", "currency_name">
  suppliers: IRecord<"suppliers", "currency_name">[]
  product_group?: IRecord<"product_groups", "products_price_method">
  product_group_discounts: (IRecord<"product_group_discounts", "discount_percent_value"> & {
    product_group: IRecord<"product_groups", "code">
  })[]
  price_band_products: (IRecord<
    "price_band_products",
    "price_pennies" | "discount_percent_value" | "preferred_price"
  > & {
    price_bands: IRecord<
      "price_bands",
      | "name"
      | "manually_created"
      | "currency_name"
      | "price_change_requires_approval"
      | "applyOpportunityCertificateBreakdown"
    >
  })[]
  universal_price_band_products: (IRecord<"universal_price_band_products", "price_pennies"> & {
    universal_price_band: IRecord<
      "universal_price_bands",
      "name" | "applyOpportunityCertificateBreakdown" | "is_shared"
    >
  })[]
  branch?: IRecord<"branches", "currency_name">
  product_master?: IRecord<
    "product_masters",
    "clearance_price_uk_pennies" | "clearance_price_ireland_pennies"
  >
}

export interface IProductPrice {
  price_band_product_id?: string
  name: string
  price_pennies: number | null
  discount_percent_value: number | null
  preferred_price: boolean
  sourceType:
    | "standard"
    | "product_group_discount"
    | "eos_price_band"
    | "sage_price_band"
    | "universal_price_band"
    | "shared_price_band"
    | "clearance"
  sourceName: any
  priceBandId?: string
  currency_name?: string
  applyOpportunityCertificateBreakdown: boolean
  requiresApprovalForChange: boolean
}

const productAttributes = (customerId: string) => [
  "sale_price_pennies",
  ...productAttrsForCalculateCostPriceFromCurrency,
  {
    name: "product_master",
    attributes: ["clearance_price_uk_pennies", "clearance_price_ireland_pennies"],
  },
  {
    as: "universal_price_band_products",
    value: {
      path: [
        "product_master",
        {
          name: "universal_price_band_products",
          attributes: [
            "price_pennies",
            {
              name: "universal_price_band",
              attributes: ["name", "is_shared", "applyOpportunityCertificateBreakdown"],
            },
          ],
          filter: {
            and: [
              { matches: { price_pennies: { gt: 0 } } },
              {
                exists: {
                  path: [
                    "universal_price_band",
                    { name: "customers", filter: { matches: { id: customerId } } },
                  ],
                },
              },
            ],
          },
        },
      ],
    },
  },
  {
    name: "price_band_products",
    attributes: [
      "price_pennies",
      "discount_percent_value",
      "preferred_price",
      {
        name: "price_bands",
        attributes: [
          "name",
          "manually_created",
          "currency_name",
          "price_change_requires_approval",
          "applyOpportunityCertificateBreakdown",
        ],
      },
    ],
    filter: {
      and: [
        { matches: { deleted: false } },
        {
          exists: {
            path: ["price_bands", { name: "customers", filter: { matches: { id: customerId } } }],
          },
        },
        { empty: { path: ["price_bands", "created_universal_price_band"] } },
      ],
    },
  },
  "product_group",
  {
    as: "product_group_discounts",
    value: {
      path: [
        "product_group",
        {
          name: "product_group_discounts",
          attributes: ["discount_percent_value", { name: "product_group", attributes: ["code"] }],
          filter: {
            matches: { customers: { some: { id: customerId } } },
          },
        },
      ],
    },
  },
]

export async function fetchPricesForCustomer(
  skeem: IWebClient,
  productId: string,
  customerId: string,
) {
  const prices = await fetchBulkPricesForCustomer(skeem, [productId], customerId)
  return prices[productId] ?? []
}

export async function fetchBulkPricesForCustomer(
  skeem: IWebClient,
  productIds: string[],
  customerId: string,
): Promise<Record<string, IProductPrice[]> /* productId => prices */> {
  const products = await skeem.fetch2<IProduct[]>({
    model: {
      name: "products",
      attributes: productAttributes(customerId),
      filter: { matches: { id: { or: productIds } } },
    },
  })

  const prices = products.reduce((acc, product) => {
    acc[product.id] = processPrices(product)
    return acc
  }, {} as Record<string, IProductPrice[]>)

  return prices
}

function processPrices(product: IProduct) {
  const clearancePrice = getClearancePriceForProduct(product)
  const prices: IProductPrice[] = [
    ...(clearancePrice != null
      ? [
          {
            name: "Deals",
            price_pennies: clearancePrice.price_pennies,
            discount_percent_value: null,
            preferred_price: false,
            sourceType: "clearance" as const,
            sourceName: "Deals",
            currency_name: clearancePrice.currency_name,
            applyOpportunityCertificateBreakdown: false,
            requiresApprovalForChange: false,
          },
        ]
      : []),
    ...product.universal_price_band_products.map(item => ({
      price_band_product_id: item.id,
      name: item.universal_price_band.name,
      price_pennies: item.price_pennies,
      discount_percent_value: null,
      preferred_price: false,
      sourceType: item.universal_price_band.is_shared
        ? ("shared_price_band" as const)
        : ("universal_price_band" as const),
      sourceName: item.universal_price_band.name,
      priceBandId: item.universal_price_band.id,
      currency_name: product.branch?.currency_name,
      applyOpportunityCertificateBreakdown:
        item.universal_price_band?.applyOpportunityCertificateBreakdown ?? false,
      requiresApprovalForChange: false,
    })),
    {
      name: "Standard",
      price_pennies: product.sale_price_pennies ?? 0,
      discount_percent_value: 0,
      preferred_price: false,
      sourceType: "standard" as const,
      sourceName: "standard" as const,
      currency_name: product.branch?.currency_name,
      applyOpportunityCertificateBreakdown: false,
      requiresApprovalForChange: false,
    },
    ...product.product_group_discounts.map(discount => ({
      name: `Product Group Discount`,
      price_pennies: Math.round(
        (product.sale_price_pennies ?? 0) * ((100 - (discount.discount_percent_value ?? 0)) / 100),
      ),
      discount_percent_value: discount.discount_percent_value,
      preferred_price: false,
      sourceType: "product_group_discount" as const,
      sourceName: discount.product_group.code,
      currency_name: product.branch?.currency_name,
      applyOpportunityCertificateBreakdown: false,
      requiresApprovalForChange: false,
    })),
    ...product.price_band_products
      .filter(product => product.price_bands != null)
      .map(product => ({
        price_band_product_id: product.id,
        name: product.price_bands.name,
        price_pennies: product.price_pennies,
        discount_percent_value: product.discount_percent_value,
        preferred_price: product.preferred_price,
        sourceType: product.price_bands.manually_created
          ? ("eos_price_band" as const)
          : ("sage_price_band" as const),
        sourceName: product.price_bands.name,
        priceBandId: product.price_bands.id,
        currency_name: product.price_bands.currency_name,
        applyOpportunityCertificateBreakdown:
          product.price_bands.applyOpportunityCertificateBreakdown ?? false,
        requiresApprovalForChange: !!product.price_bands.price_change_requires_approval,
      })),
  ]
  return sortBy(prices, price => price.price_pennies)
}
